import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { trackCodeChange } from '@theme/lijmkeuze-analytics';

/** Kortingscode voor gratis lijm in de lijmkeuze-testarm (SHO-10800); zie mixed-offer.js. */
const FREE_VIA_CODE = 'GRATISLIJM';

/**
 * Lijmkeuze-meting: wat een geaccepteerde code met GRATISLIJM deed.
 * @param {string[]} before codes vóór de poging
 * @param {string[]} base codes waarnaast de code uiteindelijk paste
 */
function acceptedOutcome(before, base) {
  const hadFreeVia = before.some((code) => code.toUpperCase() === FREE_VIA_CODE);
  if (!hadFreeVia) return 'toegepast';
  return base === before ? 'naast_gratislijm' : 'vervangt_gratislijm';
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      // Lijmkeuze (SHO-10800): GRATISLIJM gaat niet samen met een andere product- of orderkorting.
      // Eerst proberen we de code náást GRATISLIJM (een verzendcode mag erbij). Weigert Shopify hem
      // daar, dan nog eens in plaats van GRATISLIJM: zo vervangt een eigen code de gratis lijm, en
      // dat is de keuze. mixed-offer.js zet GRATISLIJM terug zodra de eigen code weer weg is.
      const allDiscounts = this.#existingDiscounts();
      if (allDiscounts.includes(discountCodeValue)) return;
      const withoutFreeVia = allDiscounts.filter(
        (code) => code.toUpperCase() !== FREE_VIA_CODE || discountCodeValue.toUpperCase() === FREE_VIA_CODE
      );
      const attempts = [allDiscounts];
      if (withoutFreeVia.length !== allDiscounts.length) attempts.push(withoutFreeVia);

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      /** @type {any} */
      let data;
      let existingDiscounts = allDiscounts;
      for (const base of attempts) {
        existingDiscounts = base;
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...base, discountCodeValue].join(','),
            sections: [this.dataset.sectionId],
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        data = await response.json();
        const rejected = data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        });
        if (!rejected) {
          trackCodeChange(data, {
            actie: 'toegevoegd',
            uitkomst: acceptedOutcome(allDiscounts, base),
            code: discountCodeValue,
          });
          // Pas geaccepteerd zonder GRATISLIJM: deze code botst ermee. mixed-offer.js probeert
          // GRATISLIJM er dan niet meer naast.
          if (base !== allDiscounts) {
            try {
              const conflicts = JSON.parse(sessionStorage.getItem('hds_freevia_conflicts') || '{}') || {};
              conflicts[discountCodeValue.toUpperCase()] = true;
              sessionStorage.setItem('hds_freevia_conflicts', JSON.stringify(conflicts));
            } catch (e) {}
          }
          break;
        }
        if (base === attempts[attempts.length - 1]) {
          trackCodeChange(data, { actie: 'toegevoegd', uitkomst: 'geweigerd', code: discountCodeValue });
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          // Ongeldige code: de gratis lijm die we ervoor weghaalden, terugzetten.
          if (base !== allDiscounts) {
            await fetch(Theme.routes.cart_update_url, {
              ...fetchConfig('json', { body: JSON.stringify({ discount: allDiscounts.join(',') }) }),
            }).catch(() => {});
          }
          return;
        }
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      trackCodeChange(data, { actie: 'weggehaald', code: discountCode });

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  if (!customElements.get('cart-discount-component')) customElements.define('cart-discount-component', CartDiscount);
}
